import React from 'react'
import { type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { Typography } from '~/components/elements/Typography'
import {
  ConditionOperationSelector,
  type ConditionOperationSelectorProps,
} from '~/components/features/ConditionOperationSelector'
import { SelectorListContainer } from '~/components/features/SelectorListContainer'
import {
  SurveyGroupQuestionSelector,
  type SurveyGroupQuestionSelectorProps,
} from '~/components/features/SurveyGroupQuestionSelector'
import { useFormField } from '~/hooks/useFormField'
import { compact, findByPath, getByPath } from '~/utils'

import { type InputValues, type SurveyGroupQuestion } from '../utils'

import { AnswerOptionsInput } from './AnswerOptionsInput'

export type QuestionInputProps = {
  control: Control<InputValues>
  filterGroupIndex: number
  filterIndex: number
  questions: SurveyGroupQuestion[]
}

export const QuestionInput = ({
  control,
  filterGroupIndex,
  filterIndex,
  questions,
}: QuestionInputProps) => {
  const { t } = useTranslation()
  const namePrefix =
    `filterGroups.${filterGroupIndex}.${filterIndex}.question` as const
  const questionIdField = useFormField(
    control,
    `${namePrefix}.surveyGroupQuestionId`,
  )
  const operationField = useFormField(control, `${namePrefix}.operation`)
  const answerOptionsField = useFormField(
    control,
    `${namePrefix}.answerOptions`,
  )

  const getDefaultAnswerOptions = React.useCallback(
    (questionId: SurveyGroupQuestion['id']) => {
      const question = findByPath(questions, 'id', questionId)
      const answerOption = getByPath(question, 'answerOptions.0')

      return compact([answerOption])
    },
    [questions],
  )

  const handleQuestionChange = React.useCallback<
    EH<SurveyGroupQuestionSelectorProps<SurveyGroupQuestion>, 'onChange'>
  >(
    (questionId) => {
      questionIdField.onChange(questionId)

      const answerOptions = match(operationField.value)
        .with('equal', 'not_equal', () => getDefaultAnswerOptions(questionId))
        .with('in', 'not_in', () => [])
        .exhaustive()
      answerOptionsField.onChange(answerOptions)
    },
    [
      answerOptionsField,
      getDefaultAnswerOptions,
      operationField.value,
      questionIdField,
    ],
  )

  const handleOperationChange = React.useCallback<
    EH<ConditionOperationSelectorProps, 'onChange'>
  >(
    (operation) => {
      const prevOperation = operationField.value
      const prevAnswerOptions = answerOptionsField.value

      operationField.onChange(operation)

      if (
        ['un', 'not_in'].includes(prevOperation) &&
        ['equal', 'not_equal'].includes(operation) &&
        prevAnswerOptions.length !== 1
      ) {
        const answerOptions = match(prevAnswerOptions.length)
          .with(0, () => getDefaultAnswerOptions(questionIdField.value))
          .otherwise(() => prevAnswerOptions.slice(0, 1))
        answerOptionsField.onChange(answerOptions)
      }
    },
    [
      answerOptionsField,
      getDefaultAnswerOptions,
      operationField,
      questionIdField.value,
    ],
  )

  const answerOptions = React.useMemo(() => {
    const question = findByPath(questions, 'id', questionIdField.value)

    return question?.answerOptions ?? []
  }, [questionIdField.value, questions])

  return (
    <SelectorListContainer>
      <Typography fontWeight={700} variant="heading3">
        {t('questionnaire_6')}
      </Typography>

      <SurveyGroupQuestionSelector
        questions={questions}
        {...questionIdField}
        onChange={handleQuestionChange}
      />

      <Typography fontWeight={700} variant="heading3">
        {t('search_condition')}
      </Typography>

      <ConditionOperationSelector
        {...operationField}
        onChange={handleOperationChange}
      />

      <Typography fontWeight={700} variant="heading3">
        {t('response_value_2')}
      </Typography>

      <AnswerOptionsInput
        answerOptions={answerOptions}
        conditionOperation={operationField.value}
        {...answerOptionsField}
      />
    </SelectorListContainer>
  )
}
